/* ══════════════════════════════════════════════════════════════════
   Eine Belegzeile, wie sie auf der Rechnung steht — N5-01, 04.10.2026.

   Der Prüfschirm der Abrechnung zeigte „1 Einsatz × 260,00 · 250,00": Menge
   mal Einzel ergab 260,00, daneben standen 250,00, der Nachlass von 10,00
   stand nirgends. Auf der Rechnung (RE1028) steht es richtig: „1 Einsatz ×
   250,00 — inkl. 10,00 € Nachlass". `unit_price` einer `billing_line` ist
   der Satz **vor** Rabatt und Schichtfaktor (so rechnet `packages/fach`);
   was nachgelassen wurde, steht nur im Rechenweg.

   Hier geschieht dasselbe wie drüben (`apps/api/src/dienste/lexbeleg.ts`,
   `einzelpreisFuerLexware` und `mitte/rabattvermerk.ts`,
   `vermerkAusRechenweg`): Geht Menge × Satz nicht auf die Summe auf, steht
   der verrechnete Einzelpreis da, und der Vermerk sagt, warum. Wer vor der
   Freigabe prüft, kann so Menge × Einzel nachrechnen. Geht auch der
   verrechnete nicht auf den Cent auf (E11, drüben dann eine Pauschale),
   bleibt der Satz stehen — der Vermerk steht trotzdem daneben.

   Rein, ohne React, damit es sich ohne Browser prüfen lässt.
   ══════════════════════════════════════════════════════════════════ */

/** Was unter einer Zeile vermerkt wird — Wörter macht die Maske (i18n). */
export type Zeilenvermerk =
  | { art: "rabatt"; prozent: number }
  | { art: "nachlass"; betrag: number }
  | { art: "schichtfaktor"; faktor: number };

const cent = (x: number) => Math.round(x * 100) / 100;

/**
 * Die Vermerke aus dem Rechenweg einer Abrechnungszeile — dieselbe Lesart
 * wie `vermerkAusRechenweg` in der API: ein Rabattsatz, der wirklich etwas
 * abgezogen hat, die Summe der Betragsnachlässe, ein Schichtfaktor ≠ 1.
 */
export function vermerkeAusRechenweg(rechenweg: unknown): Zeilenvermerk[] {
  const schritte = (rechenweg as { schritte?: unknown } | null)?.schritte;
  if (!Array.isArray(schritte)) return [];
  let prozent = 0;
  let betrag = 0;
  let faktor = 1;
  for (const s of schritte as Record<string, unknown>[]) {
    if (!s || typeof s !== "object") continue;
    if (s["key"] === "preis.rabatt" && Number(s["prozent"] ?? 0) > 0 && Number(s["betrag"] ?? 0) > 0) {
      prozent = Number(s["prozent"]);
    }
    if (s["key"] === "preis.rabatt_betrag") betrag += Number(s["betrag"] ?? 0) || 0;
    if (s["key"] === "preis.schichtfaktor") faktor = Number(s["faktor"] ?? 1) || 1;
  }
  const vermerke: Zeilenvermerk[] = [];
  if (faktor !== 1 && faktor > 0) vermerke.push({ art: "schichtfaktor", faktor });
  if (prozent > 0) vermerke.push({ art: "rabatt", prozent });
  else if (betrag > 0) vermerke.push({ art: "nachlass", betrag: cent(betrag) });
  return vermerke;
}

/**
 * Der Einzelpreis, wie er auf der Rechnung steht: der Satz, wenn Menge ×
 * Satz die Summe ergibt; sonst die Summe durch die Menge, auf den Cent —
 * solange das wieder aufgeht. Sonst der Satz (drüben eine Pauschale).
 */
export function einzelWieAufDemBeleg(menge: number, satz: number, netto: number | null): number {
  if (netto === null || !(menge > 0)) return satz;
  if (cent(satz) === satz && cent(menge * satz) === cent(netto)) return satz;
  const verrechnet = cent(netto / menge);
  return cent(menge * verrechnet) === cent(netto) ? verrechnet : satz;
}
